import {InvoiceDocument} from 'common'
import AlgoliaClient, {
  INVOICE_ADMIN_PANEL_ALGOLIA_INDEX
} from 'components/AlgoliaSearch'
import Button from 'components/Button'
import {useAppContext} from 'components/Firebase/hooks'
import InputField from 'components/InputField'
import Pagination from 'components/Pagination'
import {TABLE_PAGINATION_PAGE_SIZE} from 'components/constants'
import {getFormattedDate, searchWithAlgolia} from 'components/utils'
import {Timestamp} from 'firebase/firestore'
import {debounce} from 'lodash'
import React, {useEffect, useRef, useState} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import {CheckedIcon, UncheckedIconTwo} from 'assets/images'
import Layout from '../Layout'
import {Loader} from '../Loader'
import NavBar from '../Navbar'
import withAuthorization from '../Session/withAuthorization'
import Table from '../TableComponent'
import styles from './Invoices.module.scss'

interface InvoicesProps {
  customClass: string
}
const Invoices = ({customClass}: InvoicesProps) => {
  const {state} = useLocation()
  const pageFromLocation = state?.page
  const [currentPage, setCurrentPage] = useState<number>(pageFromLocation || 1)
  const [loader, setLoader] = useState(true)
  const [invoicesData, setInvoicesData] = useState<Array<InvoiceDocument>>([])
  const [pageCount, setPageCount] = useState<number>(0)
  const [dir, setDir] = useState<{key: string; dir: string}>({
    key: '',
    dir: 'asc'
  })
  const navigate = useNavigate()
  const {
    appState: {isSuperAdmin}
  } = useAppContext()

  useEffect(() => {
    searchIndex
      .setSettings({
        customRanking: ['desc(createdAt)']
      })
      .then(() => {
        AlgoliaClient.clearCache()
        searchWithAlgolia<InvoiceDocument>({
          searchQuery: '*',
          searchIndex,
          setLoaderState: setLoader,
          setData: setInvoicesData,
          setPageCount,
          currentPage,
          sortData: false
        }).then(() => setLoader(false))
      })
  }, [])

  const getRowData = (rowData: Array<InvoiceDocument>) => {
    return (rowData || []).map((singleRowData, index: number) => {
      const {
        invoiceId,
        createdAt,
        salePrice,
        buyerAccountName,
        sellerAccountName,
        productTitle,
        invoiceMarkedAsClosed
      } = singleRowData
      const date =
        typeof createdAt === 'number'
          ? Timestamp.fromMillis(createdAt)
          : createdAt
      return (
        <tr
          key={`role-management-row-data-${index}`}
          onClick={() => {
            navigate('/invoices/single-invoice-detail', {
              state: {
                id: invoiceId,
                isEdit: true,
                currentPage
              },
              preventScrollReset: true
            })
          }}
        >
          <td>{productTitle || '-'}</td>
          <td>{buyerAccountName || '-'}</td>
          <td>{sellerAccountName || '-'}</td>
          <td>{salePrice || '-'}</td>
          <td>
            <img
              src={invoiceMarkedAsClosed ? CheckedIcon : UncheckedIconTwo}
              alt="active"
            />
          </td>
          <td>{getFormattedDate(date) || '-'}</td>
        </tr>
      )
    })
  }

  const scrollRef: React.RefObject<HTMLDivElement> = React.createRef()

  const scrollToRef = () =>
    scrollRef?.current?.scrollIntoView({behavior: 'smooth'})

  const items =
    invoicesData.map((data) => ({
      ...data,
      date: data?.createdAt || 0
    })) || invoicesData

  const showPagination = pageCount > TABLE_PAGINATION_PAGE_SIZE

  const onPageChange = (page: number) => {
    setLoader(true)
    setCurrentPage(page)
    debouncedSearch?.current({
      searchQuery: searchValue,
      searchIndex,
      setLoaderState: setLoader,
      setData: setInvoicesData,
      setPageCount,
      currentPage: page,
      sortData: false
    })
    setLoader(false)
    scrollToRef()
  }

  const tableTitles = [
    {title: 'Equipment Title', sortBy: () => sortBy(['productTitle'])},
    {title: 'Buyer Account Name', sortBy: () => sortBy(['buyerAccountName'])},
    {title: 'Seller Account Name', sortBy: () => sortBy(['sellerAccountName'])},
    {title: 'Sale Price', sortBy: () => sortBy(['salePrice'])},
    {title: 'Marked as Closed'},
    {title: 'Date', sortBy: () => sortBy(['createdAt'])}
  ]

  const [searchValue, setSearchValue] = useState('')
  const searchIndex = AlgoliaClient.initIndex(INVOICE_ADMIN_PANEL_ALGOLIA_INDEX)
  const debouncedSearch = useRef(
    debounce(searchWithAlgolia<InvoiceDocument>, 800)
  )

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event?.target?.value
    setSearchValue(value)
    if (value) {
      debouncedSearch?.current({
        searchQuery: value,
        searchIndex,
        setLoaderState: setLoader,
        setData: setInvoicesData,
        setPageCount,
        sortData: false
      })
    } else {
      debouncedSearch?.current({
        searchQuery: '*',
        searchIndex,
        setLoaderState: setLoader,
        setData: setInvoicesData,
        setPageCount,
        currentPage,
        sortData: false
      })
    }
  }

  const sortBy = async (keys: Array<string>) => {
    setLoader(true)
    let newDir = 'asc'
    if (dir.key === keys[0]) {
      newDir = dir.dir === 'asc' ? 'desc' : 'asc'
    }
    setDir({dir: newDir, key: keys[0]})
    await searchIndex.setSettings({
      customRanking: keys.map((key) => newDir + '(' + key + ')')
    })
    setCurrentPage(1)
    await AlgoliaClient.clearCache()
    setTimeout(() => {
      debouncedSearch?.current({
        searchQuery: searchValue,
        searchIndex,
        setLoaderState: setLoader,
        setData: setInvoicesData,
        setPageCount,
        currentPage: 1,
        sortData: false
      })
    }, 1000)
  }
  return (
    <div className={styles.withLayout}>
      <Layout />
      <div className={[styles.container, customClass].join(' ')}>
        <NavBar title="Invoices" />
        <div className={styles.button_filters}>
          <InputField
            name="invoiceSearchField"
            type="text"
            value={searchValue}
            onChange={handleSearch}
            customClass={styles.inputField}
            placeholder="Search for Filters"
            isSearch={true}
          />
          {isSuperAdmin && (
            <Button
              customClass={styles.create_account_btn}
              text="Generate Invoice"
              onClick={() => navigate('/invoices/create-invoice')}
            />
          )}
        </div>
        {loader ? (
          <Loader />
        ) : (
          <React.Fragment>
            <Table
              customClass={styles.threedetails}
              titles={tableTitles}
              rowData={getRowData(items)}
            />
            {showPagination && (
              <div className="pagination__wrapper">
                <Pagination
                  currentPage={currentPage}
                  totalCount={pageCount}
                  pageSize={TABLE_PAGINATION_PAGE_SIZE}
                  onPageChange={onPageChange}
                />
              </div>
            )}
          </React.Fragment>
        )}
      </div>
    </div>
  )
}

export default withAuthorization(Invoices)
